<template>
  <div ref="map" class="map" />
  <div ref="popup" id="tooltip" role="tooltip">
    <div
      v-for="(selectedCC, index) in selectedCCs"
      :key="index"
      class="ccenter-item"
      :class="{ 'mb-3': selectedCCs.length > 1 }"
    >
      <div class="headline">
        <a
          v-if="isHomeSite"
          :href="getCCLink(selectedCC.id)"
        >
          {{ selectedCC.name }}
        </a>
        <router-link
          v-if="!isHomeSite"
            :to="{
              name: 'counseling-center',
              params: { ccid: selectedCC.id }
              //params: { ccid: center.id,  topics: topics, region: region, lang: lang, cType: cType, targetGroup: targetGroup}
            }"
          >
          {{ selectedCC.name }}
        </router-link>
      </div>
      <div class="text">
        {{ selectedCC.address?.zipCode }} {{ selectedCC.address?.city }}, {{ selectedCC.address?.streetName }} {{ selectedCC.address?.houseNumber }}
      </div>
      <div class="text" v-if="!isHomeSite && selectedCC.phone">{{ selectedCC.phone }}</div>
      <div class="text" v-if="!isHomeSite && selectedCC.email">{{ selectedCC.email }}</div>
    </div>
    <!-- <div id="arrow" data-popper-arrow></div> -->
  </div>
  <div
    v-if="showSearchAreaOverlay && showSearchAreaButton"
    class="search-area-button"
    ref="areaSearchButton"
  >
    <button @click="emitAreaSearch">
      {{ $t('viewer.areaSearchButton') }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { OLMap } from "@/gis/OLMap";
import { CouncelingCenterLayerObject } from "@/gis/GISApi";
import { CounselingCenterSearchResultDto } from "@/generated/openapi/api";
import Overlay from 'ol/Overlay';
import { createPopper } from '@popperjs/core';
import {toLonLat} from "ol/proj";
import {getBottomRight, getCenter, getTopRight} from "ol/extent";
import {getDistance} from "ol/sphere";
import CONFIG from "@/config/config"
import { sanitizeUrl } from '@braintree/sanitize-url';

export default defineComponent({
  name: "Viewer",
  data: function() {
    return {
      olMap: {} as OLMap,
      popup: new Overlay({
        element: this.$refs.popup as HTMLElement
      }),
      selectedCCs: {} as CounselingCenterSearchResultDto[],
      showSearchAreaButton: false,
      isHomeSite: document && document.body && document.body.classList.contains("is-home"),
    };
  },
  props: {
    cCenters: Array as PropType<CounselingCenterSearchResultDto[]>,
    selectedCouncelingCenterLayerObject: {} as PropType<CouncelingCenterLayerObject>,
    showSearchAreaOverlay: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getCCLink(id: string) {
      return sanitizeUrl(CONFIG.baseSearchHref + '#/counseling-center/' + id);
    },
    setLocationOnMap() {
      // console.log("setLocationOnMap called")
      if (this.koordinaten && this.koordinaten.length == 2) {
        // console.log("found coords: " + this.koordinaten)
        this.olMap.setLocation(this.koordinaten[0], this.koordinaten[1]);
        this.olMap.addGeolocation();
        // this.olMap.setRadius(this.koordinaten[0], this.koordinaten[1], this.entfernung);
        //this.olMap.zoomToRadius();
      } else {
        // this.olMap.removeLocation();
        // this.olMap.removeRadius();
        this.olMap.zoomToCouncelingCenters();
        // this.olMap.removeGeolocation();
      }
    },
    preparePins() {
      const locations: CouncelingCenterLayerObject[] = [];

      (this.cCenters || []).filter(cen => {
        return cen.location && cen.location.longitude && cen.location.latitude;
      }).forEach(center => {
        let isCourt = false;
        const allCourts = this.$store.state.rawLookUpValuesCourt;
        if (allCourts){
          if (center.lookupValueIds) {
            isCourt = center.lookupValueIds.some(id => {
              if (allCourts.get(id) == true) {
                return true
              } else {
                return false
              }
              // return (this.$store.state.rawLookUpValuesCourt.get(id) ? true : false);
            })
          }
        }
        locations.push({
          id: center.id,
          longitude: center.location ? center.location.longitude || 0 : 0,
          latitude: center.location ? center.location.latitude || 0 : 0,
          isCourt: isCourt
        })
      })

      return locations;
    },
    emitAreaSearch() {

      const map = this.olMap;
      const extent = map.view.calculateExtent(map.map.getSize());
      const center = toLonLat(getCenter(extent));
      const topRight = toLonLat(getTopRight(extent));
      const bottomRight = toLonLat(getBottomRight(extent));
      const distance = getDistance(topRight, bottomRight) / 2;

      this.$emit("updateFromAreaSearchButton", {
        lonLat: center,
        distance: distance
      })
    }
  },
  computed: {
    koordinaten: {
      get(): number[] {
        return this.$store.state.selectedFilters.coords;
      },
      set(value: number[]) {
        this.$store.commit("setSelectedCoords", value);
      }
    },
    /*entfernung: {
      get(): number {
        return this.$store.state.selectedFilters.range;
      },
      set(value: number) {
        this.$store.commit("setSelectedRange", value);
      }
    },*/
  },
  emits: ['updateSelectedCouncelingCenter', 'updateFromAreaSearchButton'],
  mounted() {
    setTimeout(() => {
      this.olMap = new OLMap(this.$refs.map as HTMLElement, {
        showCouncelingCenterOverlay: (counselingCenters: CouncelingCenterLayerObject[], evt: any) => {

          const filteredcCenters = (this.cCenters || []).filter(i => (counselingCenters || []).some( slo => i.id === slo.id)) || undefined ;
          // console.log("found filtered centers: ", filteredcCenters)

          if (filteredcCenters.length > 0) {
            this.selectedCCs = filteredcCenters;
            const element = popup.getElement() as HTMLElement;
            const tooltip = this.$refs.popup as HTMLElement;
            const coordinate = evt.mapBrowserEvent.coordinate;

            popup.setPosition(coordinate);

            // activate hidden popup
            (this.$refs.popup as HTMLElement).style.display = "block";

            // this is necessary in order to position the overlay centered
            const computeStylesCenterTooltip = {
              name: 'computeStyles',
              enabled: true,
              fn(data: any) {
                data.state.styles.popper = {
                  ...data.state.styles.popper,
                  inset: 'auto auto 0px 0px',
                  transform: 'translate(-88px, -8px)' // if it stays width: 11rem see #tooltip
                }
                return data.state
              },
            };

            createPopper(element, tooltip, {
              placement: 'top',
              modifiers: [computeStylesCenterTooltip]
            }).update();

          } else {
            // close popup
            (this.$refs.popup as HTMLElement).style.display = "none";
          }

          this.$emit("updateSelectedCouncelingCenter", {
            cCenter: filteredcCenters
          })
        },
        showAreaSearchButton: ((value: boolean) => {
          this.showSearchAreaButton = value;
        })
      });

      this.olMap.setCouncelingCenters(this.preparePins());
      if (this.selectedCouncelingCenterLayerObject) {
        this.olMap.selectCouncelingCenter(this.selectedCouncelingCenterLayerObject);
      }
      this.setLocationOnMap();

      const popup = new Overlay({
        element: this.$refs.popup as HTMLElement
      });
      this.olMap.addOverlay(popup);

    }, 0);


  },
  watch: {
    cCenters: function() {
      this.olMap.setCouncelingCenters(this.preparePins());
      this.setLocationOnMap();
    },
    /*entfernung: function() {
      this.setLocationOnMap();
    },*/
    koordinaten: {
      handler: function () {
        this.setLocationOnMap();
      },
      deep: true
    },
    selectedCouncelingCenterLayerObject: {
      handler: function() {
        // console.log("SELECT CCENTER LAYER: " + this.selectedCouncelingCenterLayerObject)
        if (this.selectedCouncelingCenterLayerObject) {
          this.olMap.selectCouncelingCenter(this.selectedCouncelingCenterLayerObject);
        }
      },
      deep: true
    }
  },
  beforeUnmount() {
    if (this.olMap) {
      this.olMap.onDestroy();
    }
  }
});
</script>
<style lang="scss" scoped>
.map {
  width: 100%;
  height: 25rem;
}

@media screen and (max-width: 768px){
  .map{
    height: calc(100vh - 86px);
  }
}

.frauen #tooltip .headline{
  color: #870038;
}

.familie #tooltip .headline{
  color: #217cab;
}

#tooltip {
  display: inline-block;
  background: #ffffff;
  color: #643045;
  font-weight: bold;
  padding: 5px 10px;
  font-size: 13px;
  border-radius: 4px;
  box-shadow: -4px 4px 8px -2px grey;
  width: 15rem;
  max-height: 200px;
  overflow: auto;
  line-height: 1.25;

  .headline{
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 6px;
    word-break: break-word;

    a, a:hover{
      text-decoration: none;
    }
  }

  .text{
    color: #000;
    font-size: 12px;
    margin-bottom: 5px;
  }
}

#arrow,
#arrow::before {
  position: absolute;
  width: 8px;
  height: 8px;
  background: inherit;
}

#arrow {
  visibility: hidden;
}

#arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}

#tooltip[data-popper-placement^='top'] > #arrow {
  bottom: -4px;
}

#tooltip[data-popper-placement^='bottom'] > #arrow {
  top: -4px;
}

#tooltip[data-popper-placement^='left'] > #arrow {
  right: -4px;
}

#tooltip[data-popper-placement^='right'] > #arrow {
  left: -4px;
}

.ccenter-item {
  max-height: 250px;
}
.search-area-button {
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  bottom: 20px;
  width: 60%;
}

.frauen .search-area-button button {
  border: 0px solid #870038;
  background-color: #870038;
  padding: 7px 15px;
  color: #FFF;
  cursor: pointer;
  font-weight: 700;
}


.familie .search-area-button button {
  border: 0px solid #217cab;
  background-color: #217cab;
  padding: 7px 15px;
  color: #FFF;
  cursor: pointer;
  font-weight: 700;
}
</style>
